// GENERATED by scripts/apply-brand.mjs from brand/generic/brand.config.js.
// Do not edit — your changes will be overwritten on the next build.
// ─────────────────────────────────────────────────────────────────────────
// Brand: generic — the neutral, unbranded Presentation Studio.
//
// This file is ALSO the authoring baseline: the colour literals below are the
// ones written by hand throughout src/, and brandCss() (src/js/brand.js)
// translates them to whichever brand is active. Keep it dependency-free — the
// build copies it verbatim to src/generated/brand.js.
// ─────────────────────────────────────────────────────────────────────────

export const BRAND = {
  id: "generic",
  // <title> and the sign-in gate headline.
  name: "Presentation Studio",
  // Toolbar lockup: mark + short name.
  mark: "◆",
  shortName: "Studio",
  // How the ✓ Review panel refers to the approved colour set.
  paletteLabel: "theme palette",
  // Title of the built-in starter deck.
  starterTitle: "Welcome to Presentation Studio",
};

// Slide palette — referenced as P.<name> across the editor. The ✓ Review
// palette check, the swatches and the gradient presets all derive from it.
export const P = {
  // Base surfaces & primary accent
  deep:    "#0B1026",
  dark:    "#161D38",
  card:    "#1C2547",
  purple:  "#6C5CE7",
  light:   "#E9ECFF",
  magenta: "#E15FD5",
  teal:    "#0FA396",
  // Bright accents — identical across brands
  cyan:    "#00D4FF",
  electric:"#7DD3FC",
  gold:    "#FFD740",
  green:   "#69F0AE",
  red:     "#FF5252",
  orange:  "#FF8A65",
  // Text tones
  white:   "#FFFFFF",
  dim:     "rgba(233,236,255,0.65)",
  muted:   "rgba(233,236,255,0.4)",
  faint:   "rgba(233,236,255,0.15)",
};

// Ordered accent ramp for data visualisation: chart series, pie slices, smart
// objects, diagram nodes. A brand that has a prescribed colour order for
// infographics expresses it here rather than having call sites pick hues.
export const SERIES = [P.cyan, P.magenta, P.gold, P.green, P.orange, P.electric, P.purple, P.teal];

// Typography rules the brand imposes on deck content. The generic brand keeps
// the studio's own micro-typography: uppercase mono eyebrows, centred text.
export const TYPE = {
  microCase: "uppercase",   // kickers, card tags, container labels, stat captions
  microFont: "mono",        // which FONTS role that micro-type is set in
  microTracking: 5,         // default kicker letter-spacing, px
  align: "center",          // default alignment for new text elements
  headLineHeight: 1.02,     // heading leading
  // Slide leading, not web-article leading. These were 1.6 and 1.45, which is
  // right for a column of prose somebody reads at their own pace and too loose
  // for a slide: the same outline that fitted one Telia page (1.2) ran onto a
  // second here, and the second page of each pair carried two bullets and a
  // repeated heading. Still airy — this brand's character — just not by a whole
  // slide. See TABLE_LEADING in src/js/brand.js for the same argument about a
  // grid, where a cell holding "150 ms" is not a paragraph at all.
  bodyLineHeight: 1.5,      // body-copy and card-body leading
  listLineHeight: 1.35,     // list-item leading
  listMarker: "◆",          // default list bullet
};

// Guideline don'ts enforced by the ✓ Review panel. The generic brand states
// none: every palette colour may be used anywhere.
export const RULES = { neverText: [], neverBackground: [], clashes: [], noAllCaps: false };

// Editor-chrome colours that sit outside the slide palette: the app shell,
// menus, bars and scrims. `*Rgb` entries are bare "r,g,b" triples because they
// are used at many different alphas.
export const CHROME = {
  bg:          "#0A0F22", // app shell background
  menu:        "#171F3A", // dropdown menus, dialogs, FABs
  hover:       "#7D6EF5", // primary-button hover
  stageTop:    "#171F3C", // stage column radial gradient …
  stageBottom: "#090D1E", // … and its outer stop
  diagramTop:  "#141b34", // diagram-preview radial gradient inner stop
  numAccent:   "#8B7BF0", // slide numbers in generated index pages
  accent:      "#00D4FF", // the editor's interactive accent: focus, hover, selection
  accentRgb:   "0,212,255",
  ok:          "#69F0AE", // saved / passing
  warn:        "#FFD740", // off-brand colour flags, fit warnings
  danger:      "#FF5252", // destructive actions
  lightRgb:    "233,236,255", // P.light
  purpleRgb:   "108,92,231",  // P.purple
  darkRgb:     "22,29,56",    // P.dark
  barRgb:      "12,17,36",    // toolbar / status-bar wash
  scrimRgb:    "7,10,22",     // overlay scrim
  scrim2Rgb:   "6,9,18",      // dialog backdrop
};

// Theme font roles. `head`'s first family is the heading marker (see
// PPTX.headingMarker).
//
// Display and body are two genuinely different faces, which is what makes the
// role split mean anything: a heading, a card title and a quote read as
// display type, body copy and bullets as reading type, and the per-element
// Font controls have two distinguishable things to choose between. Space
// Grotesk is a self-hosted open-licence face (see fonts/ below); body stays on
// the system stack, which is a good reading face on every platform and costs
// nothing to ship.
export const FONTS = {
  head: "'Space Grotesk', system-ui, -apple-system, 'Segoe UI', sans-serif",
  body: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  mono: "'JetBrains Mono', ui-monospace, 'SFMono-Regular', monospace",
};

// @font-face rules to emit into src/index.html. `file` is relative to this
// brand folder; faces whose file is missing are skipped, so removing the file
// below degrades to the system stack rather than breaking the build.
//
// `embedInExports` also inlines the faces as base64 in exported decks and
// generated pages, so they keep the brand's typography on a machine that does
// not have the fonts installed. It costs roughly +33% of the faces' total size
// per exported file, and it redistributes the font files with every export —
// which is why the face here is openly licensed (fonts/OFL.txt).
//
// One variable file covers the whole 300–700 range the studio asks of the
// display role, so the weight is declared as a range rather than a value —
// `font-weight: 300 700` is what a variable face wants, and the build
// interpolates it verbatim. The file's internal family name is "Space Grotesk
// Light" (its default instance); the @font-face rule overrides that, which is
// why `family` here is the name everything else uses.
//
// Embedded in exports: at ~30 kB of base64 for a single face it is cheap, and
// without it an exported deck loses the display face and every role collapses
// back onto the system stack — which is the situation this pairing exists to
// end.
export const fonts = {
  embedInExports: true,
  faces: [
    { family: "Space Grotesk", weight: "300 700", style: "normal", file: "fonts/SpaceGrotesk-Variable.woff2" },
  ],
};

// No Word report template. Export → Word document builds the studio's own
// look from the palette; see brand/telia for what a brand with one declares.
export const DOCX = null;

// PowerPoint export. `headingMarker` is the CSS family that means "theme
// heading"; `headingFace` is the GDI font name to write for those runs, or
// null to let PowerPoint use its own theme font. `theme` is an optional
// clrScheme/fontScheme to stamp on the exported file so a recipient editing
// it in PowerPoint picks brand colours from the theme swatches; null leaves
// PowerPoint's own Office theme in place.
export const PPTX = {
  // The CSS family that means "theme heading". `headingFace` stays null: the
  // display face is shipped for the web, not installed on the machines that
  // open an exported .pptx, so heading runs are left to PowerPoint's own theme
  // font rather than naming a face the recipient almost certainly lacks. Name
  // it here if the font is rolled out to those machines.
  headingMarker: "Space Grotesk",
  headingFace: null,
  // `bodyMarker` + `weightFaces` name the GDI families for weights PowerPoint
  // cannot reach with the bold flag — a Medium or Light face usually installs
  // as its own family rather than as a weight of the body family. The body
  // role here is the system stack, which has no such families.
  bodyMarker: null,
  weightFaces: {},
  theme: null,
};

// Standing slide furniture — a brand mark and/or a confidentiality marking
// printed on every slide, the way a corporate template puts them on its slide
// master. `{ first, rest }` — the cover and everything after it, since a
// template usually treats the two differently — each `{ label, logo }` with
// either half optional. See brand/telia for a worked example, and
// brand/README.md for the geometry (stage px on the 1280×720 stage). The
// generic studio marks nothing.
export const FURNITURE = null;

// What to do with a deck imported from someone else's template. The generic
// studio has no palette to enforce — its colours are a good default, not a
// rule — so an imported deck keeps the look it arrived with. A brand with a
// prescribed palette sets this to true (see brand/telia).
export const IMPORT = { rebrand: false };

// The starter deck's cover slide: `image` is artwork the brand prints behind
// its front page, and `title` / `sub` / `meta` the boxes it lays the cover out
// on (stage px on the 1280×720 stage). See brand/telia for a worked example —
// a corporate template usually has both, prescribed to the point. Generic has
// no artwork of its own, so its cover keeps the studio's own layout over a
// live background; the geometry below is that layout.
export const COVER = {
  image: null,
  title: { x: 90, y: 200, w: 1100, h: 220, size: 96, lineHeight: 1.02 },
  sub: { x: 90, y: 440, w: 640, h: 95, size: 30 },
  meta: { x: 90, y: 570, w: 400, h: 95, size: 18 },
};

// An icon library for the Insert → Icon picker: a folder of SVGs plus the
// colour roles to tokenise (see brand/telia for a worked example). A brand
// without one keeps the plain glyph field, which is what generic does.
// The page a picture shares with its copy: the picture fills the right half of
// the slide edge to edge and the copy runs down the left. A brand with a
// template of its own states that template's geometry here (see brand/telia,
// where every number traces to a placeholder in the official .pptx); this is
// the generic equivalent, on the studio's own margins.
export const SPLIT = {
  title: { x: 90, y: 72, w: 620, h: 120, size: 52 },
  // The column is a comfortable measure rather than the whole gap to the
  // picture: the layout runs it wider when a slide's copy will not fit,
  // and it needs somewhere to run to.
  copy: { x: 90, y: 232, w: 520, h: 416 },
  picture: { x: 700, y: 0, w: 580, h: 720 },
};

// How a brand draws data — the shape, ink and weight of its infographics, as
// distinct from the colours in P and the ordered ramp in SERIES. A corporate
// identity usually has a chapter on this, and where it does, the diagram
// builder, the smart objects and the chart renderer should be reading it
// rather than each carrying a look of its own (brand/telia's traces line by
// line to the Infographics chapter of the Telia Brand Hub).
//
// This is the generic baseline: the studio's own dark, layered look — soft
// corners, tinted gradients, a coloured glow — kept exactly as it was before
// the block existed.
// The grounds a deck is built on: the ordinary content page, and the page that
// carries a statement alone (a chapter divider, a pull quote, a heading with
// nothing under it). A brand whose template alternates between a light content
// page and a dark statement page says so here. The studio's own look is dark
// throughout, so both are the same colour and nothing alternates.
export const GROUNDS = {
  content: P.deep,
  statement: P.deep,
};

export const INFOGRAPHIC = {
  // Corner radius for a diagram node, a group container and a smart object's
  // panels. `null` leaves each block's own default alone.
  radius: null,
  // Flat fill only — solid colour, hairline border, no gradient, glow, bevel
  // or sheen sweep. Generic keeps the depth.
  flat: false,
  // The inks an arrow may be drawn in, as a list — a brand that gives arrows a
  // colour of their own usually gives them one per ground, and whichever reads
  // on the page is used. `null` means an arrow takes the colour of whatever it
  // points at, which is the studio default.
  arrow: null,
  // Stroke width for connectors, chart lines and rules.
  lineWidth: 4,
  // Radius of the round marker on each point of a line chart; 0 for none.
  marker: 0,
  // Font role for a large figure that IS the content — a counter's value, a
  // chart's data label. "head" or "body".
  dataFont: "head",
  // Weight for a sum or total, which a chart may set apart from its parts.
  totalWeight: 700,
};

export const icons = null;

// Favicon: a file for the app shell (path relative to this brand folder, like
// every file reference here), plus a tiny inline mark that exported/generated
// HTML files carry with them. The inline mark must not use url(#…) paint
// servers — Chrome rejects those on file:// pages.
export const favicon = {
  file: "assets/favicon.svg",
  inline: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="#00D4FF"/><rect x="12" y="16" width="40" height="28" rx="4" fill="#0B1026" opacity="0.85"/><path d="M27 23l14 7-14 7z" fill="#FFFFFF"/><rect x="22" y="48" width="20" height="3.5" rx="1.75" fill="#0B1026" opacity="0.85"/></svg>',
};

// Extra CSS for exported decks and generated pages, appended after any faces
// embedded by `fonts.embedInExports` — e.g. @font-face rules pointing at a
// public font URL instead of inlining the files.
export const exportFontCss = "";

// No house diagrams. The Insert → Diagram picker offers this brand only
// Eraser's own kinds; see brand/telia for what a brand with one looks like.
export const DIAGRAMS = [];
